import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Stethoscope,
  UserCog,
  BarChart3,
  CreditCard,
  Settings,
  ChevronLeft,
  ChevronRight,
  HeartPulse,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const Sidebar = ({ collapsed, onToggle }) => {
  const { hasRole, hasPermission, hasAnyPermission } = useAuth();
  const { buildUrl, isFeatureEnabled } = useClinic();
  const location = useLocation();

  const getNavItems = () => {
    const items = [];

    // Dashboard - Requires DASHBOARD_OVERVIEW permission (or fallback)
    if (isFeatureEnabled('dashboard') && hasPermission('DASHBOARD_OVERVIEW')) {
      items.push({
        title: 'Dashboard',
        subpath: 'dashboard',
        href: buildUrl('dashboard'),
        icon: LayoutDashboard,
      });
    }

    // Patients - Requires PATIENT_READ permission
    if (isFeatureEnabled('patients') && hasPermission('PATIENT_READ')) {
      items.push({
        title: 'Patients',
        subpath: 'patients',
        href: buildUrl('patients'),
        icon: Users,
      });
    }

    // Appointments - Requires APPOINTMENT_READ permission
    if (isFeatureEnabled('appointments') && hasPermission('APPOINTMENT_READ')) {
      items.push({
        title: 'Appointments',
        subpath: 'appointments',
        href: buildUrl('appointments'),
        icon: Calendar,
      });
    }

    // Consultations - Requires CONSULTATION_READ or OPD_QUEUE_MANAGE
    if (isFeatureEnabled('consultations') && hasAnyPermission(['CONSULTATION_READ', 'OPD_QUEUE_MANAGE'])) {
      items.push({
        title: 'Consultations',
        subpath: 'consultations',
        href: buildUrl('consultations'),
        icon: Stethoscope,
      });
    }

    // Doctors - Requires STAFF_MANAGE or USER_READ
    if (isFeatureEnabled('doctors') && hasAnyPermission(['STAFF_MANAGE', 'USER_READ'])) {
      items.push({
        title: 'Doctors Directory',
        subpath: 'doctors',
        href: buildUrl('doctors'),
        icon: UserCog,
      });
    }

    // Reports - Requires REPORT_READ permission
    if (isFeatureEnabled('reports') && hasPermission('REPORT_READ')) {
      items.push({
        title: 'Reports & Analytics',
        subpath: 'reports',
        href: buildUrl('reports'),
        icon: BarChart3,
      });
    }

    // Payments & Collections - Controlled by feature setting 'payments'
    if (isFeatureEnabled('payments')) {
      items.push({
        title: 'Payments',
        subpath: 'payments',
        href: buildUrl('payments'),
        icon: CreditCard,
      });
    }

    // Settings - Requires FEATURE_SETTINGS_MANAGE permission
    if (hasPermission('FEATURE_SETTINGS_MANAGE')) {
      items.push({
        title: 'PMS Settings',
        subpath: 'settings',
        href: buildUrl('settings'),
        icon: Settings,
      });
    }

    return items;
  };

  const navItems = getNavItems();

  return (
    <TooltipProvider delayDuration={100}>
      <aside
        data-testid="sidebar"
        className={cn(
          'fixed top-0 left-0 z-50 h-screen bg-slate-900 text-slate-200 border-r border-slate-800 transition-all duration-300 flex flex-col print:hidden',
          collapsed ? 'w-[72px]' : 'w-64'
        )}
      >
        {/* Brand Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="h-9 w-9 rounded-xl bg-teal-600 flex items-center justify-center text-white shrink-0 shadow-sm shadow-teal-500/20">
              <HeartPulse className="h-5 w-5" />
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <span className="font-bold text-base tracking-tight text-white font-heading leading-tight">
                  PulseFlow
                </span>
                <span className="text-[10px] text-teal-400 font-medium tracking-wider uppercase">
                  Clinical PMS
                </span>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="hidden md:flex h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation Items */}
        <ScrollArea className="flex-1 py-4 px-3">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.endsWith(`/${item.subpath}`) ||
                (item.subpath !== 'dashboard' && location.pathname.includes(`/${item.subpath}`));

              const linkContent = (
                <NavLink
                  to={item.href}
                  data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-teal-600 text-white shadow-sm shadow-teal-900/20'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/80',
                    collapsed && 'justify-center px-2'
                  )}
                >
                  <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-white' : 'text-slate-400')} />
                  {!collapsed && <span className="truncate">{item.title}</span>}
                </NavLink>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.subpath}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 font-medium">
                      {item.title}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return <div key={item.subpath}>{linkContent}</div>;
            })}
          </nav>
        </ScrollArea>

        {/* Footer info when expanded */}
        {!collapsed && (
          <div className="p-4 border-t border-slate-800/80 text-[11px] text-slate-500 flex items-center justify-between">
            <span>PMS Clinical UI</span>
            <span className="text-teal-500 font-mono">v2.0</span>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
